import { createElement, FC, Suspense, useMemo } from 'react'
import { useAtomValue } from 'jotai'
import { NotFoundErrorBoundary } from 'src/components/insights/not-found-error-boundary'
import { FullScreenLoading } from 'src/components/full-screen-loading'
import { InsightToolbar } from 'src/v2/views/owner-reveal/insights/toolbar'
import { Colors, Shapes, printStyle, themeSpacing } from 'src/components/theme'
import { FlexColumn } from 'src/components/flex-containers'
import styled from '@emotion/styled'
import { FANCY_SCROLLBAR } from 'src/utils/styles'
import { ErrorBoundary } from 'src/components/errors/error-boundary'
import { InsightDownload } from 'src/views/insight-download'
import * as coreAtoms from 'src/v2/state/core'
import { Insight } from 'src/components/insights/types'
import { fullAddress } from 'src/utils/full-address'
import { CircleIcon } from 'src/components/icons/circle-icon'
import { AlphamapLogoThrobber } from 'src/components/alphamap-logo/throbber'
import { formatString } from 'src/utils/format-string'

const CONTENT_MAX_WIDTH = 1130

const print = printStyle`
  height: 100%;
  width: 100%;
  background: white;
  padding: 0 !important;
`

const ContentSpacer = styled.div<{ withoutSpacing?: number }>`
  & > div:first-of-type {
    max-width: ${({ withoutSpacing }) => (withoutSpacing ? '100%' : `${CONTENT_MAX_WIDTH}px`)};
    margin-inline: ${({ withoutSpacing }) => (withoutSpacing ? 0 : 'auto')};
  }
`

const Page = styled.div<{ withoutSpacing?: number }>`
  width: 100%;
  height: 100%;
`

const Content = styled(ContentSpacer)`
  overflow-x: hidden;
  overflow-y: scroll;
  overflow-y: overlay;
  height: 100%;
  @media (max-width: 1600px) {
    padding-inline: ${({ withoutSpacing }) => (withoutSpacing ? 0 : `${themeSpacing(5)}px`)};
  }
  padding-top: 20px;
  ${print}
  ${FANCY_SCROLLBAR}
  padding-bottom: 112px;
  background-color: ${Colors.neutral50};
`

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 70vh;
`

const Loader = () => (
  <LoaderWrapper>
    <AlphamapLogoThrobber />
  </LoaderWrapper>
)
type InsightPageProps = {
  contentWithoutSpacing?: boolean
  children: React.ReactNode
}
const Container: FC<InsightPageProps> = ({ children, contentWithoutSpacing }) => {
  return (
    <Page withoutSpacing={contentWithoutSpacing ? 1 : 0}>
      <ErrorBoundary>{children}</ErrorBoundary>
    </Page>
  )
}

type InsightContainerProps = {
  children: React.ReactNode
  insight: Insight
  errorMessage?: string
  title?: string
  subtitle?: string
  contentWithoutSpacing?: boolean
  downloadable?: boolean
  downloadFileName?: string
  customActions?: React.ReactNode
  color?: string
  iconBgColor?: string
}
export const InsightContainer: FC<InsightContainerProps> = ({
  children,
  errorMessage,
  insight,
  title: titleProp,
  subtitle: subtitleProp,
  contentWithoutSpacing,
  downloadable = true,
  downloadFileName,
  customActions,
  color,
  iconBgColor,
}) => {
  const title = titleProp ?? insight.name
  const selectedEntity = useAtomValue(coreAtoms.selectedEntity)

  const subtitle = useMemo(() => {
    if (subtitleProp) {
      return subtitleProp
    }

    if (selectedEntity?.type === 'SELECTED_OWNER') {
      return formatString(selectedEntity.data.name, 'name')
    }

    if (selectedEntity?.type === 'SELECTED_PARCEL') {
      return formatString(selectedEntity.data.address, 'address')
    }

    if (selectedEntity?.type === 'SELECTED_AREA') {
      return ''
    }

    if (selectedEntity?.type === 'SELECTED_LOCATION' || selectedEntity?.type === 'SELECTED_SHOPPING_CENTER') {
      return `${selectedEntity.data['am-loc'].name} · ${fullAddress(selectedEntity.data['am-loc'])}`
    }

    if (selectedEntity?.type === 'SELECTED_ADDRESS') {
      return `${selectedEntity.data.name} · ${selectedEntity.data.formatted_address}`
    }
    if (selectedEntity?.type === 'SELECTED_BRAND') {
      return selectedEntity.data.name
    }
  }, [selectedEntity, subtitleProp])

  const titleIcon = useMemo(() => {
    if (!insight.nameIcon) {
      return null
    }

    return createElement(insight.nameIcon as React.FC<{ size?: number; color?: string; style?: React.CSSProperties }>, {
      size: Shapes.insightsPageTopIconSize,
      color: 'white',
      style: { verticalAlign: 'sub' },
    })
  }, [insight.nameIcon])

  if (!selectedEntity) {
    return null
  }

  return (
    <Container contentWithoutSpacing={contentWithoutSpacing}>
      <NotFoundErrorBoundary title={title} message={errorMessage ?? 'No data available'}>
        <Suspense fallback={<FullScreenLoading />}>
          <InsightToolbar
            title={title}
            icon={insight.icon}
            iconSize={insight.iconSize}
            subtitle={subtitle ?? ''}
            actions={
              <>
                {customActions}
                {downloadable && <InsightDownload fileName={downloadFileName ?? insight.name} />}
              </>
            }
            color={color}
            iconBgColor={iconBgColor}
            titleIcon={
              titleIcon && (
                <CircleIcon
                  size={Shapes.insightsPageTopIconSize}
                  padding={5}
                  color={insight.nameIconColor || Colors.blueDark}>
                  {titleIcon}
                </CircleIcon>
              )
            }
          />
          <Content withoutSpacing={contentWithoutSpacing ? 1 : 0}>
            <FlexColumn gap={Shapes.spacing[6]}>
              <Suspense fallback={<Loader />}>{children}</Suspense>
            </FlexColumn>
          </Content>
        </Suspense>
      </NotFoundErrorBoundary>
    </Container>
  )
}
